import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getManagerDashboard, processSignoff, scheduleReview, updateReview, getManagerReviews, getManagerTriage, GetManagerDashboardOutputType, GetManagerReviewsOutputType, GetManagerTriageOutputType } from 'zite-endpoints-sdk';
import StaffReadinessIntegration from '../components/admin/StaffReadinessIntegration';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Users, ClipboardCheck, Star, Plus, Loader2, AlertTriangle, ChevronRight, ShieldAlert, Flame, Wrench, BookOpen, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

type Data = GetManagerDashboardOutputType;
type Signoff = Data['signoffQueue'][0];
type ReviewData = GetManagerReviewsOutputType;
type Review = ReviewData['reviews'][0];

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [signoffModal, setSignoffModal] = useState<Signoff | null>(null);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');

  // Reviews state
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [outcomeDialog, setOutcomeDialog] = useState<Review | null>(null);
  const [reviewForm, setReviewForm] = useState({ employeeId: '', reviewTitle: '', reviewDate: '', objectives: '' });
  const [outcomeForm, setOutcomeForm] = useState({ rating: '', notes: '', actionPoints: '', nextReviewDate: '' });
  const [savingReview, setSavingReview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [triage, setTriage] = useState<GetManagerTriageOutputType | null>(null);
  const [triageFilter, setTriageFilter] = useState<string>('all');

  const load = () => {
    if (!user) return;
    getManagerDashboard({}).then(setData).finally(() => setLoading(false));
  };

  const loadReviews = () => {
    if (!user) return;
    getManagerReviews({}).then(setReviewData).catch(() => {});
  };

  const loadTriage = () => {
    if (!user) return;
    getManagerTriage({}).then(setTriage).catch(() => {});
  };

  useEffect(() => { load(); loadReviews(); loadTriage(); }, [user]);

  const handleScheduleReview = async () => {
    if (!reviewForm.employeeId || !reviewForm.reviewTitle || !reviewForm.reviewDate) {
      toast.error('Employee, title and date are required');
      return;
    }
    setSavingReview(true);
    try {
      await scheduleReview({
        employeeId: reviewForm.employeeId,
        reviewTitle: reviewForm.reviewTitle,
        reviewDate: reviewForm.reviewDate,
        objectives: reviewForm.objectives || undefined,
      });
      toast.success('Review scheduled');
      setReviewDialogOpen(false);
      setReviewForm({ employeeId: '', reviewTitle: '', reviewDate: '', objectives: '' });
      loadReviews();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSavingReview(false); }
  };

  const handleRecordOutcome = async () => {
    if (!outcomeDialog) return;
    setSavingReview(true);
    try {
      await updateReview({
        id: outcomeDialog.id,
        rating: outcomeForm.rating ? parseInt(outcomeForm.rating) : undefined,
        notes: outcomeForm.notes || undefined,
        actionPoints: outcomeForm.actionPoints || undefined,
        nextReviewDate: outcomeForm.nextReviewDate || undefined,
      });
      toast.success('Review outcome recorded');
      setOutcomeDialog(null);
      setOutcomeForm({ rating: '', notes: '', actionPoints: '', nextReviewDate: '' });
      loadReviews();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSavingReview(false); }
  };

  const handleAction = async () => {
    if (!signoffModal || !action) return;
    setProcessing(true);
    try {
      await processSignoff({ signoffId: signoffModal.id, action, notes });
      toast.success(action === 'approve' ? 'Module signed off ✓' : 'Sent back to trainee');
      setSignoffModal(null);
      setAction(null);
      setNotes('');
      load();
    } catch { toast.error('Failed to process sign-off'); }
    finally { setProcessing(false); }
  };

  if (loading) return (
    <div className="p-8 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
  );

  const statusDot = (completed: number, total: number) => {
    if (total === 0) return null;
    const pct = completed / total;
    return (
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${pct === 1 ? 'bg-primary' : pct > 0 ? 'bg-accent' : 'bg-muted'}`} />
        <span className="text-xs text-muted-foreground">{Math.round(pct * 100)}%</span>
      </div>
    );
  };

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">Manager Dashboard</h1>
        <p className="text-sm text-muted-foreground">{data?.signoffQueue.length ?? 0} pending sign-off{(data?.signoffQueue.length ?? 0) !== 1 ? 's' : ''}</p>
      </div>

      {/* Triage summary cards */}
      {triage && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-6">
          {[
            { label: 'Sign-offs', count: triage.summary.pendingSignoffs, color: 'text-primary', link: '#signoffs' },
            { label: 'Compliance', count: triage.summary.complianceGaps, color: 'text-destructive', link: '#triage' },
            { label: 'Reviews', count: triage.summary.upcomingReviews, color: 'text-primary', link: '#reviews' },
            { label: 'Maintenance', count: triage.summary.maintenanceOpen, color: 'text-muted-foreground', link: '/maintenance' },
            { label: 'Onboarding', count: triage.summary.onboardingPending, color: 'text-primary', link: '/admin?tab=onboarding' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-lg p-3 text-center">
              <p className={`text-xl font-bold ${s.count > 0 ? s.color : 'text-muted-foreground'}`}>{s.count}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Link to="/team-roster" className="inline-flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors no-underline">
          <Users className="w-4 h-4 text-primary" /> Team Roster
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </Link>
        <Link to="/training-videos" className="inline-flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors no-underline">
          <BookOpen className="w-4 h-4 text-primary" /> Learning Library
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </Link>
      </div>

      {/* BMS Staff Hub Sync Status */}
      <div className="mb-6">
        <StaffReadinessIntegration />
      </div>

      <Tabs defaultValue={triage && triage.items.filter(i => i.severity === 'critical').length > 0 ? 'triage' : data?.signoffQueue && data.signoffQueue.length > 0 ? 'signoffs' : 'team'}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="triage" className="gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Triage
            {triage && triage.items.filter(i => i.severity === 'critical').length > 0 && (
              <span className="ml-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">{triage.items.filter(i => i.severity === 'critical').length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2"><Users className="w-3.5 h-3.5" /> Team</TabsTrigger>
          <TabsTrigger value="signoffs" className="gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Sign-offs
            {data?.signoffQueue && data.signoffQueue.length > 0 && (
              <span className="ml-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">{data.signoffQueue.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviews" className="gap-2"><Star className="w-3.5 h-3.5" /> Reviews</TabsTrigger>
        </TabsList>

        {/* Triage Tab */}
        <TabsContent value="triage">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Select value={triageFilter} onValueChange={setTriageFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                <SelectItem value="Training">Training</SelectItem>
                <SelectItem value="Compliance">Compliance</SelectItem>
                <SelectItem value="Reviews">Reviews</SelectItem>
                <SelectItem value="Onboarding">Onboarding</SelectItem>
                <SelectItem value="Fire Safety">Fire Safety</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground ml-auto">{triage?.items.length || 0} items</p>
          </div>
          {!triage ? <Skeleton className="h-32" /> : (() => {
            const filtered = triageFilter === 'all' ? triage.items : triage.items.filter(i => i.area === triageFilter);
            if (filtered.length === 0) return (
              <div className="text-center py-12 border border-dashed border-border rounded-lg">
                <CheckCircle className="w-8 h-8 text-primary mx-auto mb-2 opacity-40" />
                <p className="text-sm text-muted-foreground">No operational issues — all clear!</p>
              </div>
            );
            const sevColors = { critical: 'border-l-destructive bg-destructive/5', warning: 'border-l-accent bg-accent/5', info: 'border-l-border bg-muted/30' };
            return (
              <div className="space-y-2">
                {filtered.map(item => (
                  <Link key={item.id} to={item.link} className={`flex items-center gap-3 border border-border border-l-4 rounded-lg px-4 py-3 hover:bg-muted/50 transition-colors no-underline ${sevColors[item.severity]}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs">{item.area}</Badge>
                        <p className="text-sm font-medium text-foreground">{item.title}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                        {item.staffName && <span>{item.staffName}</span>}
                        {item.dueDate && <span>· Due {item.dueDate}</span>}
                        {item.subtitle && <span>· {item.subtitle}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </Link>
                ))}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="team">
          {!data?.trainees || data.trainees.length === 0 ? (
            <p className="text-muted-foreground text-sm">No trainees found. Staff need the 'Trainee' role to appear here.</p>
          ) : (
            <div className="border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Staff member</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">Dept.</th>
                    {data.categories.slice(0, 5).map(c => (
                      <th key={c.id} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">{c.name.split(' ')[0]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.trainees.map(t => (
                    <tr key={t.id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.email}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{t.department || '–'}</td>
                      {data.categories.slice(0, 5).map(c => {
                        const cp = t.categoryProgress.find(p => p.categoryName === c.name);
                        return <td key={c.id} className="px-4 py-3">{cp ? statusDot(cp.completed, cp.total) : <span className="text-xs text-muted-foreground">–</span>}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="signoffs">
          {!data?.signoffQueue || data.signoffQueue.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground">
              <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No pending sign-offs — all clear!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.signoffQueue.map(s => (
                <div key={s.id} className="border border-border bg-card p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{s.traineeName} — <span className="text-primary">{s.moduleTitle}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Quiz passed {s.requestedDate}
                      {s.quizScore !== null && ` · Score: ${s.quizScore}%`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Practical observation required before sign-off</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => { setSignoffModal(s); setAction('reject'); }}>
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                    </Button>
                    <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => { setSignoffModal(s); setAction('approve'); }}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Sign off
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">{reviewData?.reviews.length || 0} reviews</p>
            <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => setReviewDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Schedule Review
            </Button>
          </div>
          {!reviewData?.reviews.length ? (
            <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded">
              <Star className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No reviews scheduled yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reviewData.reviews.map(r => (
                <div key={r.id} className="border border-border bg-card p-4 rounded-lg flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-foreground">{r.reviewTitle}</p>
                    <p className="text-xs text-muted-foreground">{r.employeeName}{r.reviewDate ? ` · ${r.reviewDate}` : ''}</p>
                    {r.objectives && <p className="text-xs text-muted-foreground mt-1">Objectives: {r.objectives}</p>}
                    {r.status === 'Completed' && r.rating && (
                      <div className="flex gap-0.5 mt-1">
                        {[1,2,3,4,5].map(i => <Star key={i} className={`w-3 h-3 ${i <= r.rating! ? 'text-primary fill-primary' : 'text-muted-foreground'}`} />)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${r.status === 'Completed' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{r.status}</span>
                    {r.status === 'Scheduled' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setOutcomeDialog(r); setOutcomeForm({ rating: '', notes: '', actionPoints: '', nextReviewDate: '' }); }}>
                        Record Outcome
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule Performance Review</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Employee *</label>
              <Select value={reviewForm.employeeId || 'none'} onValueChange={v => setReviewForm(f => ({ ...f, employeeId: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {reviewData?.employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Review Title *</label>
              <Input value={reviewForm.reviewTitle} onChange={e => setReviewForm(f => ({ ...f, reviewTitle: e.target.value }))} placeholder="e.g. 6-Month Review" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Date *</label>
              <Input type="date" value={reviewForm.reviewDate} onChange={e => setReviewForm(f => ({ ...f, reviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Pre-review Objectives</label>
              <Textarea value={reviewForm.objectives} onChange={e => setReviewForm(f => ({ ...f, objectives: e.target.value }))} placeholder="Topics to discuss..." className="min-h-16" />
            </div>
            <Button onClick={handleScheduleReview} disabled={savingReview} className="w-full bg-primary text-primary-foreground">
              {savingReview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scheduling...</> : 'Schedule Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Outcome Dialog */}
      <Dialog open={!!outcomeDialog} onOpenChange={open => { if (!open) setOutcomeDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Review Outcome: {outcomeDialog?.reviewTitle}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Rating (1–5)</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(i => (
                  <button
                    key={i}
                    onClick={() => setOutcomeForm(f => ({ ...f, rating: i.toString() }))}
                    className={`w-9 h-9 rounded-md border text-sm font-medium transition-colors ${outcomeForm.rating === i.toString() ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary'}`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes</label>
              <Textarea value={outcomeForm.notes} onChange={e => setOutcomeForm(f => ({ ...f, notes: e.target.value }))} placeholder="Review notes..." className="min-h-16" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Action Points</label>
              <Textarea value={outcomeForm.actionPoints} onChange={e => setOutcomeForm(f => ({ ...f, actionPoints: e.target.value }))} placeholder="What needs to happen next..." className="min-h-16" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Next Review Date</label>
              <Input type="date" value={outcomeForm.nextReviewDate} onChange={e => setOutcomeForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
            </div>
            <Button onClick={handleRecordOutcome} disabled={savingReview} className="w-full bg-primary text-primary-foreground">
              {savingReview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Outcome'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign-off Modal */}
      <Dialog open={!!signoffModal} onOpenChange={open => { if (!open) { setSignoffModal(null); setAction(null); setNotes(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action === 'approve' ? '✅ Sign off module' : '❌ Send back to trainee'}</DialogTitle>
          </DialogHeader>
          {signoffModal && (
            <div className="py-2 space-y-4">
              <div className="bg-muted p-3 text-sm">
                <p><span className="text-muted-foreground">Trainee:</span> <strong>{signoffModal.traineeName}</strong></p>
                <p><span className="text-muted-foreground">Module:</span> <strong>{signoffModal.moduleTitle}</strong></p>
                {signoffModal.quizScore !== null && <p><span className="text-muted-foreground">Quiz score:</span> <strong>{signoffModal.quizScore}%</strong></p>}
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  {action === 'approve' ? 'Notes (optional)' : 'Reason / feedback for trainee (required)'}
                </label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder={action === 'approve' ? 'Any notes for the trainee...' : 'Explain what they need to work on before sign-off...'}
                  className="min-h-24" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSignoffModal(null); setAction(null); setNotes(''); }}>Cancel</Button>
            <Button
              onClick={handleAction}
              disabled={processing || (action === 'reject' && !notes.trim())}
              className={action === 'approve' ? 'bg-primary text-primary-foreground' : 'bg-destructive text-destructive-foreground'}
            >
              {processing ? 'Processing...' : action === 'approve' ? 'Confirm sign-off' : 'Send back'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
